import React, { createContext, useContext, useEffect, useState } from 'react';

import cx from 'classnames';
import { useDebounce } from 'use-debounce';
import Downshift from 'downshift';
import { ListItem, List } from '@xingternal/list';

import styles from './Autocomplete.css';

const AutoCompleteContext = createContext({});
const AutocompleteProvider = AutoCompleteContext.Provider;
const useAutocompleteState = () => useContext(AutoCompleteContext);

type AutocompleteProps = {
  selectedValue: object;
  onChange: (params: any) => void;
  onBlur?: () => void;
  callback: (params: string) => void;
  selectedPropertyName: string;
  children?: React.ReactNode;
  minChars?: number;
  timeout?: number;
  allowFreeText?: boolean;
  isOpen?: boolean;
};

type AutocompleteListItemProps = {
  id?: string;
  index?: number;
  item?: any;
  children?: React.ReactNode;
  skin?: { [key: string]: string };
};

type AutocompleteListProps = {
  children?: React.ReactNode;
  skin?: { [key: string]: string };
};

type AutocompleteInputProps = {
  children: React.ReactNode;
};

const AutocompleteListItem = ({
  id,
  index,
  skin,
  item,
  children,
  ...rest
}: AutocompleteListItemProps) => {
  const {
    getItemProps,
    highlightedIndex,
  }: { getItemProps?: any; highlightedIndex?: number } = useAutocompleteState();

  return (
    <ListItem
      key={`${id}${index}`}
      {...getItemProps({ item, index })}
      className={cx(
        styles.item,
        {
          [styles.itemHovered]: highlightedIndex === index,
        },
        skin?.listItem
      )}
      {...rest}
    >
      {children}
    </ListItem>
  );
};

const AutocompleteList = ({
  children,
  skin,
  ...rest
}: AutocompleteListProps) => {
  const { isOpen }: { isOpen?: boolean } = useAutocompleteState();

  if (!isOpen || React.Children.count(children) === 0) return null;

  return (
    <div className={styles.listWrapper}>
      <List className={cx(styles.menu, skin?.listWrapper)} {...rest}>
        {children}
      </List>
    </div>
  );
};

const AutocompleteInput = ({ children }: AutocompleteInputProps) => {
  const { getInputProps }: { getInputProps?: any } = useAutocompleteState();

  return (
    <>
      {React.Children.map(children, (child: any) =>
        React.cloneElement(child, {
          ...getInputProps(),
        })
      )}
    </>
  );
};

const Autocomplete = ({
  selectedValue,
  onChange,
  callback,
  selectedPropertyName,
  children,
  minChars = 3,
  timeout = 300,
  onBlur,
  allowFreeText = false,
}: AutocompleteProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedtext] = useDebounce(searchTerm, timeout);
  useEffect(() => {
    if (debouncedtext) callback(debouncedtext);
  }, [debouncedtext]);

  const handleStateChange = (changes: any) => {
    if (
      [
        Downshift.stateChangeTypes.blurInput,
        Downshift.stateChangeTypes.mouseUp,
      ].includes(changes.type)
    ) {
      onBlur && onBlur();
    }
    if (changes.hasOwnProperty('selectedItem')) {
      if (changes.selectedItem) {
        onChange(changes.selectedItem);
      }
    } else if (changes.hasOwnProperty('inputValue')) {
      if (changes?.inputValue?.length === 0) {
        onChange({});
        setSearchTerm('');
        onBlur && onBlur();
      }
      if (changes.inputValue && changes.inputValue.length >= minChars) {
        setSearchTerm(changes.inputValue);
      }
      if (allowFreeText) {
        onChange({
          [selectedPropertyName]: changes.inputValue,
        });
      }
    }
  };

  return (
    <Downshift
      selectedItem={selectedValue}
      itemToString={(item: any) => (item && item[selectedPropertyName]) || ''}
      onStateChange={handleStateChange}
    >
      {({
        getInputProps,
        getToggleButtonProps,
        getItemProps,
        isOpen,
        highlightedIndex,
      }) => (
        <div className={styles.rootWrapper}>
          <AutocompleteProvider
            value={{
              isOpen,
              getInputProps,
              getToggleButtonProps,
              getItemProps,
              highlightedIndex,
            }}
          >
            {children}
          </AutocompleteProvider>
        </div>
      )}
    </Downshift>
  );
};

export {
  Autocomplete,
  AutocompleteInput,
  AutocompleteList,
  AutocompleteListItem,
};
